/** PlayerSpawnPoint, Checkpoint (+ CheckpointZone visuals) and Door. */
import type { SpriteRef } from '../../../data/gameData.ts';
import type { Xform } from '../../../model/xform.ts';
import { GameBehaviour, type CheckpointLike } from '../game.ts';
import type { SimCollider, SimGo, WorldSnapshot } from '../world.ts';
import { FrameCounter, clamp01, lerp, type RGBA, type V2 } from '../unity.ts';
import { PlayerController, PlayerState, PowerupType, type DoorLike } from '../player.ts';
import { bool, num } from '../props.ts';
import { ActivatorBase } from './activators.ts';
import { playerOf, rectContains, spriteBounds } from './util.ts';

export class PlayerSpawnPoint extends GameBehaviour {
  static override checkpointable = false;
  spawnOrder = 0;
  override awake() {
    this.spawnOrder = num(this.data.spawnOrder);
    this.game.spawnPoints.push(this);
  }
  override start() {
    if (this.go.sprite) this.go.sprite.enabled = false;
  }
  override onDestroy() {
    this.game.spawnPoints = this.game.spawnPoints.filter((s) => s !== this);
  }
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** sprite-driven visuals of a checkpoint zone (frame, background and hold fill) */
export class CheckpointZone extends GameBehaviour {
  static override checkpointable = false;
  frame: SimGo | null = null;
  bg: SimGo | null = null;
  fill: SimGo | null = null;
  frameAlphaRest = 1;
  bgAlphaRest = 1;
  fillAlphaRest = 1;

  override awake() {
    this.frame = this.refGo(this.data.checkpointZoneSprite) ?? this.go;
    this.bg = this.refGo(this.data.zoneBgSprite);
    this.fill = this.refGo(this.data.zoneFillSprite);
    this.frameAlphaRest = this.frame?.sprite?.color[3] ?? 1;
    this.bgAlphaRest = this.bg?.sprite?.color[3] ?? 1;
    this.fillAlphaRest = this.fill?.sprite?.color[3] ?? 1;
  }

  private setSize(g: SimGo | null, size: V2) {
    if (g?.sprite) g.sprite.size = [size.x, size.y];
  }
  private setA(g: SimGo | null, a: number) {
    if (g?.sprite) g.sprite.color = [g.sprite.color[0], g.sprite.color[1], g.sprite.color[2], a];
  }

  setVisualState(center: V2, size: V2) {
    this.go.position = center;
    this.setSize(this.frame, size);
    this.setSize(this.bg, size);
    this.setA(this.frame, this.frameAlphaRest);
    this.setA(this.bg, this.bgAlphaRest);
    this.clearHoldFill();
  }

  setHoldFill(r: Rect, t: number) {
    t = clamp01(t);
    if (t <= 0) return this.clearHoldFill();
    const f = this.fill;
    if (!f?.sprite) return;
    const fh = r.h * t;
    f.position = { x: r.x + r.w / 2, y: r.y + fh / 2 };
    f.sprite.size = [r.w, fh];
    this.setA(f, this.fillAlphaRest);
    f.sprite.enabled = true;
  }

  clearHoldFill() {
    if (!this.fill?.sprite) return;
    this.fill.sprite.size = [0, 0];
    this.fill.sprite.enabled = false;
  }

  fadeOut(center: V2, size: V2, r: Rect, fillT: number, opacity: number) {
    opacity = clamp01(opacity);
    this.go.position = center;
    this.setSize(this.frame, size);
    this.setSize(this.bg, size);
    this.setA(this.frame, this.frameAlphaRest * opacity);
    this.setA(this.bg, this.bgAlphaRest * opacity);
    fillT = clamp01(fillT);
    if (fillT > 0 && this.fill?.sprite) {
      const fh = r.h * fillT;
      this.fill.position = { x: r.x + r.w / 2, y: r.y + fh / 2 };
      this.fill.sprite.size = [r.w, fh];
      this.setA(this.fill, this.fillAlphaRest * opacity);
      this.fill.sprite.enabled = true;
    } else this.clearHoldFill();
  }
}

const enum ZoneState {
  Idle,
  Expanding,
  Holding,
  Retracting,
  Fading,
}

/** Checkpoint is not ICheckpointable: its state survives checkpoint restores. */
export class Checkpoint extends ActivatorBase implements CheckpointLike {
  static override checkpointable = false;
  checkpointNumber = 0;
  playerDoesntTrigger = false;
  lastCheckedTime = 0;
  zone: CheckpointZone | null = null;
  zoneBounds: Rect = { x: -3, y: -2, w: 6, h: 4 };
  expandTime = FrameCounter.seconds(0.5);
  holdTime = FrameCounter.seconds(1);
  fadeOutTime = FrameCounter.seconds(0.5);
  pendingColor: RGBA = [1, 1, 1, 1];
  zoneColorCaptured = false;
  pendingSnapshot: WorldSnapshot | null = null;
  state = ZoneState.Idle;

  override awake() {
    const d = this.data;
    this.checkpointNumber = num(d.checkpointNumber);
    this.playerDoesntTrigger = bool(d.playerDoesntTrigger);
    if (d.zoneBounds) this.zoneBounds = { x: num(d.zoneBounds.x, -3), y: num(d.zoneBounds.y, -2), w: num(d.zoneBounds.width, 6), h: num(d.zoneBounds.height, 4) };
    this.expandTime = FrameCounter.from(d.expandTime, 0.5);
    this.holdTime = FrameCounter.from(d.holdTime, 1);
    this.fadeOutTime = FrameCounter.from(d.fadeOutTime, 0.5);
    for (const g of this.go.descendants()) g.noSnapshot = true;
  }

  override start() {
    this.zone = this.refBehaviour(this.data.checkpointZone, CheckpointZone) ?? this.go.getBehaviourInChildren(CheckpointZone, true);
    this.zone?.setVisualState(this.flagBottomCenter(), { x: 0, y: 0 });
  }

  private get activatorGo(): SimGo | null {
    const g = this.game.levelMeta.checkpointObject;
    return g && !g.destroyed ? g : null;
  }

  override onTriggerEnter(other: SimCollider) {
    if (!this.canStartSequence()) return;
    const act = this.activatorGo;
    if (act) {
      if (!isChildOf(other.go, act)) return;
      this.tryStartFromActivator();
      return;
    }
    if (this.playerDoesntTrigger) return;
    const p = playerOf(other);
    if (!p || p.playerState === PlayerState.Dying) return;
    this.beginExpand();
  }

  override fixedUpdate() {
    if (this.state === ZoneState.Idle) {
      if (this.activatorGo) this.tryBeginExpandFromActivatorInZone();
      return;
    }
    if (!this.zone) return;
    switch (this.state) {
      case ZoneState.Expanding:
        this.tryCaptureFirstPlayerZoneColor();
        this.expandTime.count();
        this.applyZoneVisuals(this.expandTime.percentage);
        if (this.expandTime.isFinished()) {
          this.state = ZoneState.Holding;
          this.holdTime.reset();
        }
        break;
      case ZoneState.Holding:
        this.tryCaptureFirstPlayerZoneColor();
        this.applyHoldingVisuals();
        if (this.isHoldRequirementMet()) {
          if (this.holdTime.isNotRunning()) {
            this.pendingSnapshot = this.game.captureSnapshot();
            this.holdTime.restart();
          } else this.holdTime.count();
          if (this.holdTime.isFinished()) this.trySaveAndBeginFade();
        } else {
          this.holdTime.reset();
          this.pendingSnapshot = null;
          if (this.isHoldZoneEmpty()) this.beginRetract();
        }
        break;
      case ZoneState.Retracting:
        this.expandTime.count();
        this.applyZoneVisuals(1 - this.expandTime.percentage);
        if (this.expandTime.isFinished()) this.returnToIdle();
        break;
      case ZoneState.Fading:
        this.fadeOutTime.count();
        this.applyFadeVisuals(1 - this.fadeOutTime.percentage);
        if (this.fadeOutTime.isFinished()) this.returnToIdle();
        break;
    }
  }

  private canStartSequence() {
    if (this.state !== ZoneState.Idle) return false;
    if (this.lastCheckedTime > 0) return false;
    if (this.game.currentCheckpoint === this) return false;
    const last = this.game.lastCheckpoint;
    if (last && last.checkpointNumber > this.checkpointNumber) return false;
    return true;
  }

  private tryBeginExpandFromActivatorInZone() {
    if (!this.canStartSequence()) return;
    if (!rectContains(this.worldRect(), this.activatorGo!.position)) return;
    this.tryStartFromActivator();
  }

  private tryStartFromActivator() {
    this.applyActivatorCheckpointColor();
    this.beginExpand();
  }

  private beginExpand() {
    this.zoneColorCaptured = false;
    this.pendingSnapshot = null;
    this.state = ZoneState.Expanding;
    this.expandTime.restart();
    this.holdTime.reset();
    this.fadeOutTime.reset();
    this.zone?.setVisualState(this.flagBottomCenter(), { x: 0, y: 0 });
  }

  private returnToIdle() {
    this.pendingSnapshot = null;
    this.state = ZoneState.Idle;
    this.expandTime.reset();
    this.holdTime.reset();
    this.fadeOutTime.reset();
    this.zone?.setVisualState(this.flagBottomCenter(), { x: 0, y: 0 });
  }

  private beginRetract() {
    this.pendingSnapshot = null;
    this.state = ZoneState.Retracting;
    this.expandTime.restart();
  }

  private tryCaptureFirstPlayerZoneColor() {
    if (this.zoneColorCaptured) return;
    const act = this.activatorGo;
    if (act) {
      if (!rectContains(this.worldRect(), act.position)) return;
      this.applyActivatorCheckpointColor();
      this.zoneColorCaptured = true;
      return;
    }
    const r = this.worldRect();
    for (const p of this.game.players) {
      if (!rectContains(r, p.go.position)) continue;
      this.pendingColor = [...p.color] as RGBA;
      this.zoneColorCaptured = true;
      return;
    }
  }

  private applyActivatorCheckpointColor() {
    const c = this.activatorGo?.comps.get('CheckpointObject')?.color;
    if (c) this.pendingColor = [num(c.r, 1), num(c.g, 1), num(c.b, 1), num(c.a, 1)];
  }

  private isHoldRequirementMet() {
    const act = this.activatorGo;
    if (act) return rectContains(this.worldRect(), act.position);
    const players = this.game.players;
    if (!players.length) return false;
    const r = this.worldRect();
    return players.every((p) => rectContains(r, p.go.position));
  }

  private isHoldZoneEmpty() {
    const act = this.activatorGo;
    if (act) return !rectContains(this.worldRect(), act.position);
    const r = this.worldRect();
    return !this.game.players.some((p) => rectContains(r, p.go.position));
  }

  private trySaveAndBeginFade() {
    const game = this.game;
    if (game.currentCheckpoint !== this && this.pendingSnapshot && game.applySnapshot(this.pendingSnapshot)) {
      game.currentCheckpoint = this;
      this.lastCheckedTime = game.time;
      this.activate();
      game.lastCheckpoint = this;
      if (this.go.sprite) this.go.sprite.color = [...this.pendingColor] as RGBA;
      const p = this.go.position;
      game.emit({ kind: 'checkpoint', x: p.x, y: p.y });
    }
    this.pendingSnapshot = null;
    this.state = ZoneState.Fading;
    this.expandTime.reset();
    this.holdTime.reset();
    this.fadeOutTime.restart();
  }

  worldRect(): Rect {
    const p = this.go.position;
    return { x: p.x + this.zoneBounds.x, y: p.y + this.zoneBounds.y, w: this.zoneBounds.w, h: this.zoneBounds.h };
  }

  private flagBottomCenter(): V2 {
    const b = spriteBounds(this.go);
    if (b) return { x: (b[0] + b[2]) / 2, y: b[1] };
    return this.go.position;
  }

  private applyZoneVisuals(t: number) {
    const r = this.worldRect();
    const f = this.flagBottomCenter();
    const c = { x: r.x + r.w / 2, y: r.y + r.h / 2 };
    this.zone!.setVisualState({ x: lerp(f.x, c.x, t), y: lerp(f.y, c.y, t) }, { x: r.w * t, y: r.h * t });
  }

  private applyHoldingVisuals() {
    const r = this.worldRect();
    const fillT = this.isHoldRequirementMet() && this.holdTime.isRunningNotFinished() ? this.holdTime.percentage : 0;
    this.zone!.setVisualState({ x: r.x + r.w / 2, y: r.y + r.h / 2 }, { x: r.w, y: r.h });
    this.zone!.setHoldFill(r, fillT);
  }

  private applyFadeVisuals(opacity: number) {
    const r = this.worldRect();
    this.zone!.fadeOut({ x: r.x + r.w / 2, y: r.y + r.h / 2 }, { x: r.w, y: r.h }, r, 1, opacity);
  }
}

function isChildOf(g: SimGo, root: SimGo) {
  for (let x: SimGo | null = g; x; x = x.parent) if (x === root) return true;
  return false;
}

export class Door extends ActivatorBase implements DoorLike {
  enteredPlayers = new Set<PlayerController>();
  doorCollider: SimCollider | null = null;
  spriteGo: SimGo | null = null;
  tilemapGo: SimGo | null = null;
  loadCounter = FrameCounter.seconds(1);
  originalPosition: V2 = { x: 0, y: 0 };
  override noSave = ['doorCollider', 'spriteGo', 'tilemapGo', 'originalPosition'];

  override awake() {
    this.doorCollider = this.go.colliders[0] ?? null;
    this.spriteGo = this.go.sprite ? this.go : this.go.descendants().find((g) => g.sprite) ?? null;
    this.tilemapGo = this.go.descendants().find((g) => g.tilemap) ?? null;
    this.originalPosition = this.go.position;
  }

  doorBounds(): [number, number, number, number] {
    const b = this.doorCollider?.bounds();
    if (b) return b;
    const p = this.go.position;
    return [p.x - 0.5, p.y - 0.5, p.x + 0.5, p.y + 0.5];
  }

  override fixedUpdate() {
    const col = this.doorCollider;
    if (col) {
      for (const hit of this.world.overlapCollider(col)) {
        const player = playerOf(hit);
        if (!player) continue;
        const [x0, y0, x1, y1] = this.doorBounds();
        const pp = player.go.position;
        const inBounds = pp.x >= x0 && pp.x <= x1 && pp.y >= y0 && pp.y <= y1;
        if (inBounds) player.doorInRange = this;
        else if (player.doorInRange === this && player.playerState === PlayerState.Playing) player.doorInRange = null;
      }
    }
    const range = 8, fullDist = 4, size = 3;
    let dist = range;
    for (const p of this.game.players) {
      const pc = p as unknown as PlayerController;
      if (pc.powerup?.type === PowerupType.BigDude) {
        const q = p.go.position;
        dist = Math.min(Math.hypot(q.x - this.originalPosition.x, q.y - this.originalPosition.y), dist);
      }
    }
    if (dist < range) {
      const s = (size - 1) * clamp01((range - dist) / (range - fullDist)) + 1;
      const m = this.go.local;
      const rot = Math.atan2(m.b, m.a);
      const c = Math.cos(rot), sn = Math.sin(rot);
      this.go.setLocal({ a: c * s, b: sn * s, c: -sn * s, d: c * s, tx: m.tx, ty: m.ty });
      this.go.position = { x: this.go.position.x, y: this.originalPosition.y + (s - 1) };
    }
    if (this.loadCounter.countCheckFinishReset()) this.game.showEndGame();
  }

  private tint(c: RGBA) {
    if (this.spriteGo?.sprite) this.spriteGo.sprite.color = [...c] as RGBA;
    if (this.tilemapGo?.tilemap) this.tilemapGo.tilemap.color = [...c] as RGBA;
  }

  onPlayerEntered(p: PlayerController) {
    this.tint([0, 0, 0, 1]);
    this.sound(this.enteredPlayers.size === 0 ? 'doorOpen' : 'doorEnter', p.go.position);
    if (this.enteredPlayers.size === 0) this.activate();
    this.enteredPlayers.add(p);
    if (this.game.players.length === this.enteredPlayers.size) this.loadCounter.start();
  }

  onPlayerLeft(p: PlayerController) {
    if (this.enteredPlayers.has(p)) {
      this.enteredPlayers.delete(p);
      this.loadCounter.reset();
    }
    if (this.enteredPlayers.size === 0) {
      this.tint([1, 1, 1, 1]);
      this.deactivate();
      this.sound('doorClose');
    }
  }
}

/** Sprite mask while a player is inside a door. Guests have no doorInRange, so the door is the one that contains them. */
export type DoorMask =
  | { kind: 'sprite'; sprite: SpriteRef; m: Xform; flipX: boolean; flipY: boolean }
  | { kind: 'rect'; x: number; y: number; w: number; h: number };

export function doorMaskFor(player: PlayerController): DoorMask | null {
  if (player.playerState !== PlayerState.InDoor) return null;
  const door = (player.doorInRange instanceof Door ? player.doorInRange : null) ?? doorContaining(player);
  if (!door) return null;
  const sg = door.spriteGo;
  const sp = sg?.sprite?.sprite;
  if (sg && sp && sg.sprite?.enabled) {
    const pos = sg.position;
    const local = sg.local;
    const sx = Math.hypot(local.a, local.b) || 1;
    const sy = Math.hypot(local.c, local.d) || 1;
    return { kind: 'sprite', sprite: sp, m: { a: sx, b: 0, c: 0, d: sy, tx: pos.x, ty: pos.y }, flipX: sg.sprite.flipX, flipY: sg.sprite.flipY };
  }
  const [x0, y0, x1, y1] = door.doorBounds();
  return { kind: 'rect', x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

function doorContaining(player: PlayerController): Door | null {
  const p = player.go.position;
  for (const b of player.game.behaviours) {
    if (!(b instanceof Door) || b.go.destroyed) continue;
    const [x0, y0, x1, y1] = b.doorBounds();
    if (p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1) return b;
  }
  return null;
}
